// First-run product tour. A dismissible 5-step overlay that points at
// the most important sidebar items so a new tenant lands with an
// orientation instead of staring at 14 empty pages.
//
// State lives on User.onboarding (see back/routes/onboarding.js). On
// mount we GET /status; if dismissed=false we render the popover at
// stepIndex. Skip → /dismiss. Last "Done" → /complete. Each Next
// posts /step so closing mid-tour resumes where you left off.
//
// Anchoring: each step has a CSS selector (data-tour="brands" etc.)
// attached to the sidebar label. We measure with getBoundingClientRect
// every animation frame so resize/scroll/collapse stays aligned. If a
// target isn't in the DOM (sidebar collapsed on mobile), we render
// centered instead of guessing.

import React, { useEffect, useRef, useState } from 'react';
import { getOnboardingStatus, dismissOnboarding, completeOnboarding, setOnboardingStep } from 'api/onboarding/onboarding';

const STEPS = [
  {
    selector: '[data-tour="brands"]',
    title:    'Start with a Brand',
    body:     'Create your first Brand to set the voice, products, and audience. Everything in Prigmar (posts, emails, SEO articles, chat replies) is grounded in a brand profile.',
  },
  {
    selector: '[data-tour="ideas"]',
    title:    'Ideas feed your content engine',
    body:     'The system drafts post + article ideas from your brand. Approve the ones you like and they flow into the social and SEO pipelines automatically.',
  },
  {
    selector: '[data-tour="email"]',
    title:    'Email Management',
    body:     'Connect a mailbox to classify inbound messages and auto-draft replies in your brand voice. Outbound campaigns and a deliverability warm-up are built in.',
  },
  {
    selector: '[data-tour="crm"]',
    title:    'CRM kanban — Customers + Prospects',
    body:     'Contacts auto-organize into customer / prospect states. Each state has its own automated cadence (intro, nurture, retention, escalate).',
  },
  {
    selector: '[data-tour="seo"]',
    title:    'SEO Management',
    body:     'Keyword research, briefs, articles, and FAQs all drafted with your brand voice. New connectors let you push articles straight to WordPress, Ghost, or any custom endpoint.',
  },
];

const Z = 99000;

const STYLES = {
  scrim: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    zIndex: Z,
    transition: 'background .2s ease',
  },
  spotlight: {
    position: 'fixed',
    borderRadius: 10,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 4px rgba(124, 58, 237, 0.65)',
    pointerEvents: 'none',
    transition: 'all .25s ease',
    zIndex: Z + 1,
  },
  popover: {
    position: 'fixed',
    width: 360,
    maxWidth: 'calc(100vw - 32px)',
    background: '#fff',
    color: '#111',
    borderRadius: 12,
    padding: 18,
    boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
    zIndex: Z + 2,
    transition: 'all .25s ease',
  },
  title: { fontSize: 16, fontWeight: 700, marginBottom: 6 },
  body:  { fontSize: 13, lineHeight: 1.45, color: '#374151' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 },
  pager:  { fontSize: 11, color: '#6b7280' },
  btnRow: { display: 'flex', gap: 8 },
  btnSecondary: {
    border: '1px solid #d1d5db', background: '#fff', color: '#374151',
    padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
  },
  btnPrimary: {
    border: 'none', background: '#7c3aed', color: '#fff',
    padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    fontWeight: 600,
  },
};

// Position the popover so it doesn't run off-screen. Prefer right of
// the anchor on desktop (sidebar is left); if right has no room, try
// below, then center. Returns inline-style coords.
function placePopover(rect) {
  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  const PAD = 18;
  const POP_W = 360;
  const POP_H = 200; // approximate; real height varies but is bounded
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Right of anchor (preferred for left-rail sidebars)
  const rightEdge = rect.right + PAD + POP_W;
  if (rightEdge < vw - PAD) {
    return { top: Math.min(vh - POP_H - PAD, Math.max(PAD, rect.top)), left: rect.right + PAD };
  }
  // Below anchor (fallback for compact / mobile)
  const bottomEdge = rect.bottom + PAD + POP_H;
  if (bottomEdge < vh - PAD) {
    return { top: rect.bottom + PAD, left: Math.min(vw - POP_W - PAD, Math.max(PAD, rect.left)) };
  }
  // Last resort: center
  return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
}

function spotlightRect(rect) {
  if (!rect) return null;
  const PAD = 8;
  return {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };
}

export default function OnboardingTour() {
  const [status, setStatus] = useState({ dismissed: true, stepIndex: 0, completedAt: null });
  const [loaded, setLoaded] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const rafRef = useRef(null);

  // Load the status once on mount. The endpoint requires JWT, so if
  // the user isn't logged in we just stay hidden — the call 401s,
  // setStatus stays at the default dismissed=true.
  useEffect(() => {
    let alive = true;
    getOnboardingStatus()
      .then((res) => {
        if (!alive) return;
        const d = (res && res.data) || {};
        setStatus({
          dismissed:   !!d.dismissed,
          stepIndex:   typeof d.stepIndex === 'number' ? d.stepIndex : 0,
          completedAt: d.completedAt || null,
        });
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  // Re-measure the anchor every animation frame while the tour is
  // visible. This is cheap (one DOM query per step) and survives
  // sidebar collapse + window resize without extra event listeners.
  const visible = loaded && !status.dismissed;
  const step = STEPS[Math.min(status.stepIndex, STEPS.length - 1)] || STEPS[0];
  useEffect(() => {
    if (!visible) return undefined;
    const tick = () => {
      const el = document.querySelector(step.selector);
      const rect = el ? el.getBoundingClientRect() : null;
      setAnchorRect((prev) => {
        // Only update on real changes to avoid re-renders on every frame
        if (!rect && !prev) return prev;
        if (!rect || !prev) return rect;
        if (Math.abs(rect.top - prev.top) < 0.5 && Math.abs(rect.left - prev.left) < 0.5 &&
            Math.abs(rect.width - prev.width) < 0.5 && Math.abs(rect.height - prev.height) < 0.5) {
          return prev;
        }
        return rect;
      });
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [visible, step.selector]);

  if (!visible) return null;

  const idx = Math.min(status.stepIndex, STEPS.length - 1);
  const isLast = idx >= STEPS.length - 1;
  const place = placePopover(anchorRect);
  const spot  = spotlightRect(anchorRect);

  const goSkip = () => {
    setStatus((s) => ({ ...s, dismissed: true }));
    dismissOnboarding().catch(() => { /* swallow — local state already advanced */ });
  };
  const goPrev = () => {
    if (idx === 0) return;
    const nxt = idx - 1;
    setStatus((s) => ({ ...s, stepIndex: nxt }));
    setOnboardingStep(nxt).catch(() => {});
  };
  const goNext = () => {
    const nxt = idx + 1;
    setStatus((s) => ({ ...s, stepIndex: nxt }));
    setOnboardingStep(nxt).catch(() => {});
  };
  const goDone = () => {
    setStatus((s) => ({ ...s, dismissed: true, completedAt: new Date() }));
    completeOnboarding().catch(() => {});
  };

  return (
    <>
      {spot ? (
        <div style={{ ...STYLES.spotlight, ...spot }} aria-hidden="true" />
      ) : (
        // No anchor in DOM (sidebar collapsed) → fall back to a
        // plain scrim so the popover stays readable.
        <div style={STYLES.scrim} aria-hidden="true" />
      )}
      <div style={{ ...STYLES.popover, ...place }} role="dialog" aria-label="Product tour">
        <div style={STYLES.title}>{step.title}</div>
        <div style={STYLES.body}>{step.body}</div>
        <div style={STYLES.footer}>
          <span style={STYLES.pager}>{idx + 1} / {STEPS.length}</span>
          <div style={STYLES.btnRow}>
            <button type="button" style={STYLES.btnSecondary} onClick={goSkip}>Skip</button>
            {idx > 0 && (
              <button type="button" style={STYLES.btnSecondary} onClick={goPrev}>Back</button>
            )}
            {isLast ? (
              <button type="button" style={STYLES.btnPrimary} onClick={goDone}>Done</button>
            ) : (
              <button type="button" style={STYLES.btnPrimary} onClick={goNext}>Next</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Exposed for tests + the OnboardingTour test file.
OnboardingTour.STEPS = STEPS;
OnboardingTour.placePopover = placePopover;
OnboardingTour.spotlightRect = spotlightRect;
